import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge, Button, Divider, Dropdown, Field, Input, MessageBar, Option, Spinner,
  Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Title3,
} from "@fluentui/react-components";
import { api } from "../../api/client";
import {
  useCategoryBreakdown, useLifecycleReport, useTicketTimeline,
} from "../../api/hooks";

type GroupBy = "day" | "week" | "month";

interface VolumeRow { bucket: string; created: number; resolved: number; closed: number }
interface SlaReport {
  total: number; met: number; breached: number; adherencePct: number;
  byPriority: Record<string, { met: number; breached: number }>;
}
interface AgentPerfRow {
  agentId: string; displayName: string; email: string | null;
  resolved: number; avgResolutionHours: number; slaAdherencePct: number | null;
}

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoDaysAgo(days: number) { return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10); }

export function AdminReportsPage() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [groupBy, setGroupBy] = useState<GroupBy>("day");
  const qs = `from=${from}&to=${to}`;

  const volume = useQuery({
    queryKey: ["report", "volume", from, to, groupBy],
    queryFn: () => api.get<VolumeRow[]>(`/api/reports/volume?${qs}&groupBy=${groupBy}`),
  });
  const sla = useQuery({
    queryKey: ["report", "sla", from, to],
    queryFn: () => api.get<SlaReport>(`/api/reports/sla?${qs}`),
  });
  const perf = useQuery({
    queryKey: ["report", "perf", from, to],
    queryFn: () => api.get<AgentPerfRow[]>(`/api/reports/agent-performance?${qs}`),
  });
  const lifecycle = useLifecycleReport({ from, to });
  const catBreakdown = useCategoryBreakdown({ from, to });

  // Ticket explorer
  const [explorerInput, setExplorerInput] = useState("");
  const [explorerKey, setExplorerKey] = useState<string | undefined>(undefined);
  const timeline = useTicketTimeline(explorerKey);

  return (
    <div>
      <Title3>Reports</Title3>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 12, flexWrap: "wrap" }}>
        <Field label="From"><Input type="date" value={from} onChange={(_, d) => setFrom(d.value)} /></Field>
        <Field label="To"><Input type="date" value={to} onChange={(_, d) => setTo(d.value)} /></Field>
        <Field label="Group">
          <Dropdown value={groupBy} selectedOptions={[groupBy]}
            onOptionSelect={(_, d) => setGroupBy((d.optionValue as GroupBy) ?? "day")}>
            {(["day", "week", "month"] as GroupBy[]).map((g) => <Option key={g} value={g}>{g}</Option>)}
          </Dropdown>
        </Field>
      </div>

      <h4 style={{ marginTop: 24 }}>Ticket volume</h4>
      {volume.isLoading ? <Spinner /> : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Bucket</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>Resolved</TableHeaderCell>
              <TableHeaderCell>Closed</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(volume.data ?? []).map((r) => (
              <TableRow key={r.bucket}>
                <TableCell>{r.bucket}</TableCell><TableCell>{r.created}</TableCell>
                <TableCell>{r.resolved}</TableCell><TableCell>{r.closed}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <h4 style={{ marginTop: 24 }}>SLA adherence</h4>
      {sla.isLoading ? <Spinner /> : sla.data && (
        <div>
          <p>Overall: <strong>{sla.data.adherencePct}%</strong> ({sla.data.met} met / {sla.data.breached} breached, total {sla.data.total})</p>
          <ul>{Object.entries(sla.data.byPriority).map(([p, v]) =>
            <li key={p}>{p}: {v.met} met, {v.breached} breached</li>)}</ul>
        </div>
      )}

      <h4 style={{ marginTop: 24 }}>Agent performance</h4>
      {perf.isLoading ? <Spinner /> : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Agent</TableHeaderCell>
              <TableHeaderCell>Resolved</TableHeaderCell>
              <TableHeaderCell>Avg resolution (h)</TableHeaderCell>
              <TableHeaderCell>SLA %</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(perf.data ?? []).map((r) => (
              <TableRow key={r.agentId}>
                <TableCell>{r.displayName}</TableCell>
                <TableCell>{r.resolved}</TableCell>
                <TableCell>{r.avgResolutionHours}</TableCell>
                <TableCell>{r.slaAdherencePct ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <h4 style={{ marginTop: 24 }}>Lifecycle &amp; quality</h4>
      <p style={{ color: "#605E5C", marginTop: 0 }}>
        How tickets actually move through the system in this period.
        <strong> Avg time in status</strong> shows the mean dwell time per status (e.g. how long
        the average ticket sat in <em>Open</em> before moving on).{" "}
        <strong>Reopen rate</strong> is the % of tickets that came back after being Resolved or Closed —
        a leading indicator of fix quality.{" "}
        <strong>Escalation rate</strong> is the % that were Escalated at least once.{" "}
        <strong>Avg first response</strong> is the hours between ticket creation and the first
        non-internal reply from an Agent or Admin.
      </p>
      {lifecycle.isLoading ? <Spinner /> : lifecycle.data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Stat label="Tickets created" value={String(lifecycle.data.total)} />
          <Stat label="Reopen rate" value={`${lifecycle.data.reopenRatePct}%`} sub={`${lifecycle.data.reopened} reopened`} />
          <Stat label="Escalation rate" value={`${lifecycle.data.escalationRatePct}%`} sub={`${lifecycle.data.escalated} escalated`} />
          <Stat label="Avg first response" value={lifecycle.data.avgFirstResponseHours == null ? "—" : `${lifecycle.data.avgFirstResponseHours} h`} />
          {Object.entries(lifecycle.data.avgHoursInStatus).map(([status, hours]) => (
            <Stat key={status} label={`Avg time in ${status}`} value={`${hours} h`} />
          ))}
        </div>
      )}

      <h4 style={{ marginTop: 24 }}>Category breakdown</h4>
      <p style={{ color: "#605E5C", marginTop: 0 }}>
        Where tickets come from and how each category performs. Use this to spot categories with
        high <em>SLA breach %</em> (need more staffing or better runbooks) and high <em>volume</em>
        (candidates for self-service or knowledge-base articles).
      </p>
      {catBreakdown.isLoading ? <Spinner /> : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Category</TableHeaderCell>
              <TableHeaderCell>Total</TableHeaderCell>
              <TableHeaderCell>Resolved</TableHeaderCell>
              <TableHeaderCell>Avg resolution (h)</TableHeaderCell>
              <TableHeaderCell>SLA breach %</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(catBreakdown.data ?? []).map((r) => (
              <TableRow key={r.categoryId}>
                <TableCell>{r.categoryName}</TableCell>
                <TableCell>{r.total}</TableCell>
                <TableCell>{r.resolved}</TableCell>
                <TableCell>{r.avgResolutionHours ?? "—"}</TableCell>
                <TableCell>{r.slaBreachPct ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <h4 style={{ marginTop: 24 }}>Ticket explorer</h4>
      <p style={{ color: "#605E5C", marginTop: 0 }}>
        Paste a ticket number (e.g. <code>42</code>) or its internal id to see the full
        end-to-end story: when it was created, every status change, who assigned it to whom,
        every comment and internal note, attachments, and the final rating — in chronological order.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", maxWidth: 480 }}>
        <Field label="Ticket # or id" style={{ flex: 1 }}>
          <Input
            value={explorerInput}
            onChange={(_, d) => setExplorerInput(d.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setExplorerKey(explorerInput.trim() || undefined); }}
            placeholder="e.g. 42"
          />
        </Field>
        <Button appearance="primary" onClick={() => setExplorerKey(explorerInput.trim() || undefined)}>Load timeline</Button>
      </div>
      {timeline.isFetching && <Spinner style={{ marginTop: 12 }} />}
      {timeline.isError && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>{(timeline.error as Error).message}</MessageBar>
      )}
      {timeline.data && <TimelineView data={timeline.data} />}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ padding: 12, border: "1px solid #EDEBE9", borderRadius: 6, background: "#FAF9F8" }}>
      <div style={{ fontSize: 11, color: "#605E5C", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#605E5C", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function TimelineView({ data }: { data: import("../../types/models").TicketTimeline }) {
  const { ticket, events } = data;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong>IT-{ticket.ticketNumber}</strong>
        <span>{ticket.title}</span>
        <Badge appearance="tint">{ticket.status}</Badge>
        <Badge appearance="tint" color="brand">{ticket.priority}</Badge>
        {ticket.reopenCount > 0 && <Badge appearance="tint" color="warning">Reopened ×{ticket.reopenCount}</Badge>}
      </div>
      <div style={{ fontSize: 12, color: "#605E5C", marginTop: 4 }}>
        Requester: {ticket.requester?.displayName ?? "—"} ·
        Assignee: {ticket.assignee?.displayName ?? "Unassigned"} ·
        Category: {ticket.category?.name ?? "—"} ·
        Queue: {ticket.queue?.name ?? "—"} ·
        SLA due: {ticket.slaDueAt ? new Date(ticket.slaDueAt).toLocaleString() : "—"}
      </div>
      <Divider style={{ margin: "12px 0" }} />
      <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {events.map((e, i) => (
          <li key={i} style={{ padding: "8px 0", borderBottom: "1px solid #F3F2F1" }}>
            <TimelineEntry e={e} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function TimelineEntry({ e }: { e: import("../../types/models").TimelineEvent }) {
  const time = new Date(e.at).toLocaleString();
  const who = (u: { displayName?: string } | null | undefined) => u?.displayName ?? "system";
  if (e.kind === "created") return <Row time={time} icon="🆕" title={`Created by ${who(e.actor)}`} />;
  if (e.kind === "status") return <Row time={time} icon="🔁" title={`Status: ${e.from ?? "—"} → ${e.to} (by ${who(e.actor)})`} body={e.note ?? undefined} />;
  if (e.kind === "assigned") return <Row time={time} icon="👤" title={`Assigned to ${e.assigneeName ?? "—"}${e.queueName ? ` (queue: ${e.queueName})` : ""} by ${who(e.actor)}`} />;
  if (e.kind === "comment") return <Row time={time} icon={e.isInternal ? "🔒" : "💬"} title={`${who(e.author)} ${e.isInternal ? "added an internal note" : "commented"}`} body={e.body} highlight={e.isInternal} />;
  if (e.kind === "attachment") return <Row time={time} icon="📎" title={`${who(e.uploader)} uploaded ${e.fileName} (${Math.round(e.sizeBytes / 1024)} KB)`} />;
  if (e.kind === "rating") return <Row time={time} icon="⭐" title={`${who(e.rater)} rated ${e.score}/5`} body={e.comment ?? undefined} />;
  return <Row time={time} icon="•" title={`${e.action} by ${who(e.actor)}`} />;
}

function Row({ time, icon, title, body, highlight }: { time: string; icon: string; title: string; body?: string; highlight?: boolean }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 8 }}>
      <div style={{ fontSize: 16, lineHeight: "20px" }}>{icon}</div>
      <div>
        <div>{title}</div>
        {body && (
          <div style={{ marginTop: 4, padding: 8, borderRadius: 4, background: highlight ? "rgba(255,200,0,0.12)" : "rgba(98,100,167,0.06)", whiteSpace: "pre-wrap" }}>
            {body}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#605E5C", whiteSpace: "nowrap" }}>{time}</div>
    </div>
  );
}
